import { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { type PowerInfo } from '@/lib/powers-data';
import {
  powerHasStickers,
  stickerChatCode,
  stickerImageUrl,
} from '@/lib/xat-stickers';
import { getStickerList } from '@/services/xat-proxy.service';
import {
  ORBITAL_ICON_BOX_CLASS,
  copyText,
  getPowerCodeFontSize,
  cn,
} from '@/components/help-center/latest-power-shared';
import {
  GALLERY_BOX_STYLE,
  PowerGalleryViewToggle,
  PowerSectionSmileyIcon,
  galleryBadgeClass,
  galleryCodeTextClass,
  usePowerGalleryViewMode,
  type PowerGalleryViewMode,
} from '@/components/help-center/powers/power-gallery-view';
import {
  GalleryHoverOverlay,
  galleryItemHoverClass,
  galleryMediaHoverClass,
} from '@/components/help-center/powers/gallery-hover';

interface PowerStickersSectionProps {
  power: PowerInfo;
}

function StickerCard({
  powerSlug,
  sticker,
  itemKey,
  copiedKey,
  onCopy,
  viewMode,
}: {
  powerSlug: string;
  sticker: string;
  itemKey: string;
  copiedKey: string | null;
  onCopy: (key: string, text: string) => void;
  viewMode: PowerGalleryViewMode;
}) {
  const chat = stickerChatCode(powerSlug, sticker);
  const label = viewMode === 'light' ? chat : sticker;
  const isCopied = copiedKey === itemKey;
  const [imgFailed, setImgFailed] = useState(false);

  return (
    <button
      type="button"
      onClick={() => onCopy(itemKey, chat)}
      title={`Copiar ${chat}`}
      className={cn(
        ORBITAL_ICON_BOX_CLASS,
        'group relative flex w-full min-w-0 flex-col items-center gap-2 rounded-xl p-3',
        galleryItemHoverClass(viewMode)
      )}
      style={GALLERY_BOX_STYLE[viewMode]}
    >
      <GalleryHoverOverlay mode="copy" isCopied={isCopied} />
      <div className={cn('flex h-14 w-14 items-center justify-center', galleryMediaHoverClass)}>
        {imgFailed ? (
          <span className="font-poppins text-[10px] font-semibold text-gray-400">{sticker}</span>
        ) : (
          <img
            src={stickerImageUrl(powerSlug, sticker)}
            alt={chat}
            className="max-h-[52px] max-w-[52px] object-contain"
            loading="lazy"
            decoding="async"
            onError={() => setImgFailed(true)}
          />
        )}
      </div>
      <div className={galleryBadgeClass(viewMode, isCopied)}>
        <span
          className={cn(
            'whitespace-nowrap font-poppins font-semibold leading-none',
            galleryCodeTextClass(viewMode)
          )}
          style={{ fontSize: `${getPowerCodeFontSize(label)}px` }}
        >
          {label}
        </span>
      </div>
    </button>
  );
}

export default function PowerStickersSection({ power }: PowerStickersSectionProps) {
  const mayHaveStickers = powerHasStickers(power);
  const seeded = (power.stickers ?? []).map((s) => s.trim()).filter(Boolean);
  const [stickers, setStickers] = useState<string[]>(seeded);
  const [loading, setLoading] = useState(mayHaveStickers && seeded.length === 0);
  const [failed, setFailed] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const { mode, setMode } = usePowerGalleryViewMode();
  const slug = power.slug.trim().toLowerCase();

  useEffect(() => {
    setStickers(seeded);
  }, [power.slug, power.stickers?.join('|')]);

  useEffect(() => {
    if (!mayHaveStickers || !slug) {
      setLoading(false);
      return;
    }

    if (seeded.length > 0) {
      setStickers(seeded);
      setLoading(false);
      setFailed(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setFailed(false);

    void getStickerList(slug).then((data) => {
      if (cancelled) return;
      const list = data?.power?.sticker_list;
      if (Array.isArray(list) && list.length > 0) {
        setStickers(list.map((s) => String(s).trim()).filter(Boolean));
        setFailed(false);
      } else {
        setStickers([]);
        setFailed(true);
      }
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [mayHaveStickers, slug, seeded.length, power.stickers?.join('|')]);

  const handleCopy = useCallback(async (key: string, text: string) => {
    const ok = await copyText(text);
    if (ok) {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1800);
    }
  }, []);

  if (!mayHaveStickers) return null;
  if (!loading && stickers.length === 0 && !failed) return null;

  return (
    <section className="border-b border-gray-100 bg-white py-14">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-center gap-3">
            <PowerSectionSmileyIcon code="smile" size="md" />
            <h2 className="font-poppins text-xl font-bold text-gray-900 md:text-2xl">Stickers</h2>
          </div>
          {stickers.length > 0 ? (
            <PowerGalleryViewToggle mode={mode} onChange={setMode} className="shrink-0" />
          ) : null}
        </div>

        {loading ? (
          <div className="flex items-center gap-3 py-8 text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="font-inter text-sm">A carregar stickers…</span>
          </div>
        ) : failed || stickers.length === 0 ? (
          <p className="font-inter text-sm text-gray-500">
            Não foi possível carregar a lista de stickers deste power neste momento.
          </p>
        ) : (
          <>
            <div className="mb-10 flex flex-wrap gap-3">
              <div className="inline-flex items-center gap-1.5 rounded-xl border border-pink-100 bg-pink-50/60 px-3.5 py-2.5">
                <span className="font-poppins text-sm font-semibold text-pink-800">
                  {stickers.length} {stickers.length === 1 ? 'sticker' : 'stickers'}
                </span>
              </div>
            </div>

            <div className="mb-5">
              <h3 className="font-poppins text-base font-bold text-gray-900">Coleção de stickers</h3>
              <p className="mt-1 font-inter text-sm text-gray-500">
                Stickers do power {power.name}. Clique para copiar o código no chat (ex.:{' '}
                {stickerChatCode(slug, stickers[0])}).
              </p>
            </div>

            <div className="grid grid-cols-[repeat(auto-fill,minmax(108px,1fr))] gap-2.5 overflow-visible">
              {stickers.map((sticker, index) => (
                <StickerCard
                  key={`st-${index}-${sticker}`}
                  powerSlug={slug}
                  sticker={sticker}
                  itemKey={`st-${index}-${sticker}`}
                  copiedKey={copiedKey}
                  onCopy={handleCopy}
                  viewMode={mode}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
